'use client';
import { forwardRef, useEffect, useImperativeHandle } from 'react';

import moment from 'moment';

import {
  generateSearchRooms,
  validateGuestsChildrenAges,
} from '@components/views/landing/components/booker/common/bookerUtils';
import STATIC_ROUTES from '@data/staticRoutes.json';
import clean from '@utils/cleanObject';
import { useHotelPastSearches } from '@hooks/useHotelPastSearches';
import useSearchBar from '@contexts/searchbar';
import { HotelSearchBarActionType } from '@contexts/searchbar/types/hotel';
import { useBreakpoint } from '@contexts/breakpoints';
import { useAppRouter } from '@contexts/router';

import DateSelection from './dateSelection';
import HotelSelection from './hotelSelection';
import {
  DateWrapper,
  StyledButton,
  StyledContent,
  StyledLayout,
} from './styled';
import { formatDate } from './utils';
import HotelSearchGuest from './hotelSearchGuest/HotelSearchGuest';

interface IHotelSearchBar {
  isHomePage?: boolean;
  alignVertical?: boolean;
  isTravelPlanning?: boolean;
}

export type IHotelSearchBarHandle = {
  triggerHandleSubmit: () => void;
};

const HotelSearchBar = forwardRef<IHotelSearchBarHandle, IHotelSearchBar>(
  ({ isHomePage = true, alignVertical, isTravelPlanning }, ref) => {
    const router = useAppRouter();
    const { xl } = useBreakpoint();
    const {
      pastHotelItems,
      saveHotelSearch,
      clearPastHotelSearch,
      removePastHotelSearch,
    } = useHotelPastSearches();

    const {
      state: { hotel: state },
      dispatch,
    } = useSearchBar();

    /**
     * This function is used to trigger the submit button from outside the component.
     * It is used in the drawer search bar in the hotel listing mobile header.
     */
    useImperativeHandle(ref, () => ({
      triggerHandleSubmit() {
        handleSubmit();
      },
    }));

    /**
     * This effect is used to set the initial state of the hotel search bar
     * based on the past hotel items.
     */
    useEffect(() => {
      if (!state.location && pastHotelItems && pastHotelItems.length > 0) {
        const lastSearch = pastHotelItems[0];
        dispatch({
          type: HotelSearchBarActionType.SET_LOCATION,
          payload: lastSearch,
        });
        dispatch({
          type: HotelSearchBarActionType.SET_CHECK_DATES,
          payload: {
            startDate: moment(lastSearch.in),
            endDate: moment(lastSearch.out),
          },
        });
        dispatch({
          type: HotelSearchBarActionType.SET_ROOMS,
          payload: [lastSearch.rooms?.[0]],
        });
      }
    }, [pastHotelItems, dispatch]);

    /**
     * This function is used to check the form states.
     * @returns {boolean} Returns true if the form is valid.
     */
    const checkFormState = () => {
      if (!state.location) {
        dispatch({
          type: HotelSearchBarActionType.SET_IS_LOCATION_ERROR,
          payload: true,
        });
      }

      if (!state?.checkDates?.endDate) {
        dispatch({
          type: HotelSearchBarActionType.SET_IS_CHECK_DATES_ERROR,
          payload: true,
        });
      }

      return (
        state.location &&
        state?.checkDates?.endDate &&
        !validateGuestsChildrenAges(state.rooms)
      );
    };

    /**
     * This function is used to trigger submit button.
     */
    const handleSubmit = () => {
      const isValid = checkFormState();

      if (isValid) {
        const hotelUrl = state.location?.url;
        const inDate = formatDate(state?.checkDates?.startDate);
        const outDate = formatDate(state?.checkDates?.endDate);

        const rooms = generateSearchRooms(state.rooms, true);

        const query = clean({
          in: inDate,
          out: outDate,
          room: rooms,
          status: true,
        });

        saveHotelSearch({
          dataDate: state.checkDates,
          dataHotel: state.location,
          hotelRooms: state.rooms,
        });

        state.isDrawerOpen &&
          dispatch({
            type: HotelSearchBarActionType.SET_IS_DRAWER_OPEN,
            payload: false,
          });

        router.push({
          pathname: `${STATIC_ROUTES.hotelPathPrefix}/${hotelUrl}`,
          query,
        });
      }
    };

    return (
      <StyledLayout data-testid='hotel-selection' isHomePage={isHomePage}>
        <StyledContent $alignVertical={alignVertical}>
          <HotelSelection
            history={pastHotelItems}
            clearPastHotelSearch={clearPastHotelSearch}
            removePastHotelSearch={removePastHotelSearch}
            fullWidth={alignVertical}
          />

          <DateWrapper $alignVertical={alignVertical}>
            <DateSelection isTravelPlanning={isTravelPlanning} />
          </DateWrapper>

          <HotelSearchGuest />

          {xl || xl === undefined || isHomePage ? (
            <StyledButton
              onClick={handleSubmit}
              data-testid='hotel-search-button'
              type={isHomePage ? 'primary' : 'secondary'}
              $alignVertical={alignVertical}
            >
              {isHomePage ? 'Otel Ara' : 'Yeniden Ara'}
            </StyledButton>
          ) : null}
        </StyledContent>
      </StyledLayout>
    );
  }
);

export default HotelSearchBar;
